import { useEffect, useState } from 'react';
import { useAppStore } from '../../stores/appStore';
import { useSeason } from '../../hooks/useSeason';
import { decemberCountdownLabel } from '../../lib/advent';
import { format } from 'date-fns';

export function Header() {
  const familyName = useAppStore((s) => s.familyName);
  const user = useAppStore((s) => s.user);
  const connected = useAppStore((s) => s.connected);
  const theme = useAppStore((s) => s.theme);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const logout = useAppStore((s) => s.logout);
  const layoutEditMode = useAppStore((s) => s.layoutEditMode);
  const setLayoutEditMode = useAppStore((s) => s.setLayoutEditMode);
  const openEventEditor = useAppStore((s) => s.openEventEditor);
  const setComposeAlertOpen = useAppStore((s) => s.setComposeAlertOpen);
  const calendars = useAppStore((s) => s.calendars);
  const season = useSeason();

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const timeStr = format(now, 'h:mm:ss a');
  const dateStr = format(now, 'EEE, MMM d');
  const dateStrFull = format(now, 'EEEE, MMMM d');
  const countdown = decemberCountdownLabel(now, window.location.search);

  return (
    <header className="shrink-0 border-b border-slate-800 bg-slate-950/95 backdrop-blur light:border-slate-200 light:bg-white/95">
      {/* ── Mobile: stacked, roomy layout ───────────────── */}
      <div className="flex flex-col gap-2.5 px-3 py-2.5 sm:hidden">
        {/* Row 1: brand + clock */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-base font-black text-white shadow"
              aria-hidden
            >
              F
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold leading-tight tracking-tight">
                {familyName}
              </h1>
              <p className="text-[11px] leading-tight text-slate-400">
                {dateStr}
                <span className="ml-1.5 font-semibold text-violet-300">{season.label}</span>
                {countdown && (
                  <span className="mt-0.5 block font-semibold text-violet-300">{countdown}</span>
                )}
              </p>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <time
              dateTime={now.toISOString()}
              className="block text-lg font-bold tabular-nums leading-none tracking-tight text-violet-300"
            >
              {timeStr}
            </time>
            <div className="mt-1 flex items-center justify-end gap-1.5">
              <span
                className={`inline-flex h-2 w-2 rounded-full ${
                  connected ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'
                }`}
                title={connected ? 'Live' : 'Reconnecting'}
              />
              <span
                className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-black text-white"
                style={{ background: user?.color || '#6366F1' }}
                title={user?.name}
              >
                {(user?.avatar_emoji || user?.name || '?').slice(0, 1)}
              </span>
            </div>
          </div>
        </div>

        {/* Row 2: primary actions — full width, equal buttons */}
        <div className="grid grid-cols-4 gap-2">
          <button
            type="button"
            onClick={() => setComposeAlertOpen(true)}
            className="rounded-xl bg-red-600/90 py-2.5 text-xs font-bold text-white active:scale-[0.98]"
          >
            Alert
          </button>
          <button
            type="button"
            onClick={() => openEventEditor(null)}
            className="rounded-xl bg-violet-600 py-2.5 text-xs font-bold text-white active:scale-[0.98]"
          >
            + Event
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-xl bg-slate-800 py-2.5 text-xs font-semibold text-slate-200 active:scale-[0.98]"
          >
            {theme === 'dark' ? 'Light' : 'Dark'}
          </button>
          <button
            type="button"
            onClick={logout}
            className="rounded-xl bg-slate-800 py-2.5 text-xs font-semibold text-slate-300 active:scale-[0.98]"
          >
            Lock
          </button>
        </div>
      </div>

      {/* ── Desktop / tablet ────────────────────────────── */}
      <div className="hidden items-center justify-between gap-3 px-4 py-3 sm:flex">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-lg font-black text-white shadow"
            aria-hidden
          >
            F
          </span>
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <h1 className="truncate text-lg font-bold tracking-tight md:text-xl">
                {familyName}
              </h1>
              <time
                dateTime={now.toISOString()}
                className="shrink-0 text-base font-bold tabular-nums tracking-tight text-violet-300 md:text-lg"
                title={dateStrFull}
              >
                {timeStr}
              </time>
            </div>
            <p className="text-xs text-slate-400 md:text-sm">
              {dateStrFull}
              <span className="ml-2 rounded-full bg-violet-500/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-violet-300">
                {season.label}
              </span>
              {countdown && (
                <span className="ml-2 font-semibold text-violet-300">{countdown}</span>
              )}
            </p>
          </div>
        </div>

        <div className="hidden flex-wrap items-center gap-2 lg:flex">
          {calendars
            .filter((c) => c.visible)
            .map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-800/80 px-2.5 py-1 text-xs font-medium light:bg-slate-100"
              >
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ background: c.color }}
                />
                {c.name}
              </span>
            ))}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs ${
              connected
                ? 'bg-emerald-500/15 text-emerald-400'
                : 'bg-amber-500/15 text-amber-400'
            }`}
            title={connected ? 'Live sync connected' : 'Reconnecting…'}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                connected ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'
              }`}
            />
            {connected ? 'Live' : 'Offline'}
          </span>

          <button
            type="button"
            onClick={() => setComposeAlertOpen(true)}
            className="rounded-xl bg-red-600/90 px-3 py-2 text-sm font-bold text-white shadow hover:bg-red-500"
            title="Send family alert overlay"
          >
            Alert
          </button>

          <button
            type="button"
            onClick={() => openEventEditor(null)}
            className="rounded-xl bg-violet-600 px-3 py-2 text-sm font-bold text-white shadow hover:bg-violet-500 md:px-4"
          >
            + Event
          </button>

          <button
            type="button"
            onClick={() => setLayoutEditMode(!layoutEditMode)}
            className={`rounded-xl px-3 py-2 text-sm font-semibold ${
              layoutEditMode
                ? 'bg-amber-500/20 text-amber-300'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700 light:bg-slate-200 light:text-slate-700'
            }`}
            title="Edit widget layout"
          >
            {layoutEditMode ? 'Done' : 'Widgets'}
          </button>

          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-xl bg-slate-800 px-3 py-2 text-xs font-semibold light:bg-slate-200"
            title="Toggle theme"
          >
            {theme === 'dark' ? 'Light' : 'Dark'}
          </button>

          <div className="flex items-center gap-2 rounded-xl bg-slate-800/80 px-2 py-1.5 light:bg-slate-100">
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-black text-white"
              style={{ background: user?.color || '#6366F1' }}
            >
              {(user?.avatar_emoji || user?.name || '?').slice(0, 1)}
            </span>
            <span className="max-w-[7rem] truncate text-sm font-medium">
              {user?.name}
            </span>
          </div>

          <button
            type="button"
            onClick={logout}
            className="rounded-xl px-2 py-2 text-xs text-slate-500 hover:text-slate-300"
            title="Lock"
          >
            Lock
          </button>
        </div>
      </div>
    </header>
  );
}
